/**
 * Dynamic Offers Collection Slider Statsig Tester
 */

export {};
declare global {
  interface Window {
    Insider: any
  }
}

import {
  ModelManager,
  getOrCreateStatsigManager,
  buildModelRequestPayload,
  Logger,
  syncDebugFlag,
  getStatsigClient,
  PayloadField,
  getSafeStorage,
  getCartItemsIdsString,
  getCartData,
  shouldSendPayload
} from './statsig-manager.js';
import {
  buildGatewayRequest,
  buildGatewayEndpoint,
  postGatewayRequest,
  unwrapGatewayOutputs,
} from './dyo-inference-hub-gateway-v2.js';

type MinicartServingMode = "pichi2" | "inference_hub";

interface modelResponse {
  gender_collections?: boolean;
  product_handles?: string[];
  product_handles_female?: string[];
  product_handles_male?: string[];
  render?: boolean;
  fallback_reason?: string;
  useinsider_smart_recommender_id?: string;
}

type MinicartModelResponse = modelResponse;

function normalizeMinicartInferenceHubResponse(response: unknown): MinicartModelResponse | null {
  return normalizeMinicartOutput(unwrapGatewayOutputs(response));
}

function normalizeMinicartOutput(output: unknown): MinicartModelResponse | null {
  if (!isRecord(output)) return null;

  const productHandles = stringArray(output.product_handles);
  const productHandlesFemale = stringArray(output.product_handles_female);
  const productHandlesMale = stringArray(output.product_handles_male);

  const normalized: MinicartModelResponse = {
    product_handles: productHandles,
  };

  if (typeof output.gender_collections === "boolean") {
    normalized.gender_collections = output.gender_collections;
  }
  if (productHandlesFemale.length > 0) {
    normalized.product_handles_female = productHandlesFemale;
  }
  if (productHandlesMale.length > 0) {
    normalized.product_handles_male = productHandlesMale;
  }
  if (typeof output.render === "boolean") {
    normalized.render = output.render;
  }
  if (typeof output.fallback_reason === "string") {
    normalized.fallback_reason = output.fallback_reason;
  }

  return normalized;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.length > 0) : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

interface UseInsiderCampaignData {
  campaignId: number
  variationId: number
  products: Array<{
    image_url: string
    name: string
    item_id: string
    sku: string
    size: string
    url: string
    description: string
    in_stock: number
    price: {
      BRL: number
    }
    groupcode: string
    color: string
    locale: string
    product_attributes: {
      activation_date: string
      product_type: string
      quantity: number
      shopify_tag: Array<string>
    }
    category: Array<string>
    discount: {
      BRL: number
    }
    original_price: {
      BRL: number
    }
  }>
}

type ProductsUrlList = Array<{ product_url: string }>
type ProductsURLObject = { 
  gender_collections?: boolean;
  product_handles?: Array<{ product_url: string }>;
  product_handles_female?: Array<{ product_url: string }>;
  product_handles_male?: Array<{ product_url: string }>;
 }

interface SliderElement extends HTMLElement {
  dynamicData: ProductsURLObject;
  status: "notStarted" | "pending" | "pendingForceFallback" | "ready" | "fallbackReady" | "forceNoRender";
  useDynamicRecommender: boolean
}

const MODEL_KEY = "statsig:test:dyo:collection-slider";
const EXPERIMENT = {
  title: "DyoCollection",
  name: "dyo-minicart-recommender",
  gate: "dyo-minicart-recommender",
  layer: "dyo-minicart-recommender",
};

const DEBUG = true; // Altere para false para desativar logs deste teste

if (!customElements.get("dyo-collection-slider"))
  customElements.define(
    "dyo-collection-slider",
    class DynamicOffersCollection extends HTMLElement {
      MODEL_KEY?: string
      customerId?: string;
      statsigGate: string;
      statsigLayer: string;
      statsigExperimentTitle: string;
      statsigExperimentName: string;
      modelLink: string | null;
      modelName: string | null;
      servingMode: MinicartServingMode;
      gatewayBaseUrl: string | null;
      gatewayApiKey: string | null;
      usesFeatureView: boolean;
      useInsiderSmartRecommenderId: string | null;
      statsigStableId: string | null;
      allocatedExperiment: string | null;
      updateTimeout: number;
      isGateEnabled: boolean;
      blockedUTMs: string;
      REQUEST_TIMEOUT_MS: number;
      modelManager: ModelManager;
      statsigManager: Awaited<ReturnType<typeof getOrCreateStatsigManager>>;
      elements: any;
      cart: string | null;
      cartItemsId: string | null;
      sliderId: string | null;
      utmGender: string | null;
      maxProducts: number;
      objExperimentInfos: {
        stable_id: string;
        allocated_experiment: string;
        gate: string;
        layer: string;
        cart: string;
        customer_id: string;
        category: string;
        component: string;
      };

      constructor() {
        super();
        this.customerId = this.getAttribute("data-customer-id") || null;
        this.sliderId = this.getAttribute("data-slider-id") || null;
        const paramsURL = new URLSearchParams(window.location.search);
        const utmGender = paramsURL.get("utm_gender");
        const utmContent = paramsURL.get("utm_content");
        // Mesma lógica do multiple-collections: utm_gender tem prioridade, depois utm_content
        this.utmGender = utmGender || utmContent;
        Logger.log(
          this.statsigExperimentName,
          `[DEBUG][Statsig][${this.statsigExperimentName}] UTM resolvida no DYO: utm_gender='${utmGender}', utm_content='${utmContent}', utmMatch='${this.utmGender}'`
        );
        this.statsigGate = this.getAttribute("data-statsig-gate") || EXPERIMENT.gate;
        this.statsigLayer = this.getAttribute("data-statsig-layer") || EXPERIMENT.layer;
        this.statsigExperimentTitle = this.getAttribute("data-statsig-experiment-title") || EXPERIMENT.title;
        this.statsigExperimentName = this.getAttribute("data-statsig-experiment-name") || this.statsigExperimentName;
        this.MODEL_KEY = `${this.getAttribute("data-model-key")}` || MODEL_KEY;
        this.maxProducts = parseInt(this.getAttribute("data-max-products")) || 0
        this.modelLink = null;
        this.modelName = null;
        this.servingMode = "pichi2";
        this.gatewayBaseUrl = this.getAttribute("data-gateway-base-url") || null;
        this.gatewayApiKey = null;
        this.usesFeatureView = false;
        this.useInsiderSmartRecommenderId = null
        this.statsigStableId = null;
        this.allocatedExperiment = null;
        this.updateTimeout = 0;
        this.isGateEnabled = this.getAttribute("data-is-gate-enabled") === "true"; //TODO: pegar via parametro
        this.REQUEST_TIMEOUT_MS = 4500;
        this.modelManager = new ModelManager();
        this.objExperimentInfos = {
          stable_id: this.statsigStableId,
          gate: this.statsigGate,
          layer: this.statsigLayer,
          cart: "",
          customer_id: this.customerId,
          allocated_experiment: this.allocatedExperiment,
          category: "Statsig",
          component: `${this.statsigExperimentName}`,
        }
      }

      async connectedCallback() {
        try {
          this.objExperimentInfos.cart = await getCartItemsIdsString() || "";
          syncDebugFlag(this.statsigExperimentName, DEBUG);
          this.statsigManager = await getOrCreateStatsigManager();
          await this.runTest();
        } catch (error) {
          this.logError(`[DEBUG][Statsig][${this.statsigExperimentName}] StatsigManager not ready`, false, error);
        }
      }

      async runTest() {
        try {
          Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Iniciando execução do Dynamic Offers Collection`);
          const stableContext = await getStatsigClient()?.getAsyncContext();
          this.objExperimentInfos.stable_id = stableContext?.stableID;
          this.statsigStableId = stableContext?.stableID;

          const gateOpen: boolean = await this.statsigManager.checkGate(this.statsigGate ?? "");
          const shouldPersist = gateOpen;

          if (!shouldPersist) {
            this.forceFallbackInSlider("closedGate");
            Logger.warn(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Gate closed. Exiting without persisting.`, this.objExperimentInfos);
            return;
          }

          // const triggers = ["stable_id"];
          // const shouldSend = shouldSendPayload(triggers, MODEL_KEY, "every");
          // if (!shouldSend) {
          //   Logger.warn(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Not available to send payload. Aborting...`, triggers, this.objExperimentInfos);
          //   return;
          // }
      
          const layer = await this.statsigManager.getLayerParams(this.statsigLayer ?? "");
          if (!layer) {
            this.forceFallbackInSlider("layerNotFound");
            Logger.warn(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Layer not found. Exiting.`, this.objExperimentInfos);
            return;
          }

          this.servingMode = layer.get("servingMode") === "inference_hub" ? "inference_hub" : "pichi2";
          this.gatewayBaseUrl = this.stringLayerParam(layer.get("gatewayBaseUrl")) || this.gatewayBaseUrl;
          this.gatewayApiKey = this.stringLayerParam(layer.get("gatewayApiKey")) || this.stringLayerParam(layer.get("apiKey"));
          this.modelLink = this.servingMode === "pichi2" ? this.stringLayerParam(layer.get("modelUrl")) : null;
          this.modelName = this.stringLayerParam(layer.get("modelName"));
          this.usesFeatureView = this.booleanLayerParam(layer.get("usesFeatureView"));
          this.allocatedExperiment = layer.allocatedExperimentName ?? this.allocatedExperiment;
          this.objExperimentInfos.allocated_experiment = this.allocatedExperiment;
          Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] layer`, {
            servingMode: this.servingMode,
            gatewayBaseUrl: this.gatewayBaseUrl,
            modelName: this.modelName,
            usesFeatureView: this.usesFeatureView,
            hasGatewayApiKey: Boolean(this.gatewayApiKey),
          });

          let persistedModelData = this.loadModelFromStorage();
          Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Dados do modelo persistidos anteriormente:`, persistedModelData);
          if (!persistedModelData || (!this.isHoldoutModel() && !this.isUseInsiderSmartRecommender())) {
            /**
             * Quando o modelo for random ou v0 devemos buscar o modelo sempre, pois a resposta pode variar a cada requisição mesmo que já tenhamos algo persistido.
             */
            Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Buscando dados do modelo...`);
            persistedModelData = await this.fetchDOCollectionData();
            Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Dados do modelo recebidos:`, persistedModelData);
          }

          if (!persistedModelData) {
            this.forceFallbackInSlider("modelDataNull");
            Logger.warn(
              this.statsigExperimentName,
              `[DEBUG][Statsig][${this.statsigExperimentName}] Modelo retornou null/undefined. Forçando fallback.`,
              this.objExperimentInfos
            );
            return;
          }

          if((this.isHoldoutModel() || this.isUseInsiderSmartRecommender())) {
            /**
             * Mesmo que a resposta do modelo sempre seja a mesma, é importante fazer essa requisição para popular informações ao modelo, 
             * aqui não usamos async pois não é necessário aguardar resposta.
             */
            this.fetchDOCollectionData()
          }

          Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Dados do modelo atualizado`, persistedModelData);

          if (this.isUseInsiderSmartRecommender()) {
            this.useInsiderSmartRecommenderId = persistedModelData?.useinsider_smart_recommender_id || null;
          }
    
          if((!this.isHoldoutModel() && !this.isUseInsiderSmartRecommender())) {
            const hasAnyHandles =
              (persistedModelData.product_handles && persistedModelData.product_handles.length > 0) ||
              (persistedModelData.product_handles_female && persistedModelData.product_handles_female.length > 0) ||
              (persistedModelData.product_handles_male && persistedModelData.product_handles_male.length > 0);

            if (!hasAnyHandles) {
              this.forceFallbackInSlider("emptyHandles");
              Logger.warn(
                this.statsigExperimentName,
                `[DEBUG][Statsig][${this.statsigExperimentName}] Modelo sem product_handles. Forçando fallback.`,
                persistedModelData
              );
              return;
            }
            Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Utilizando lista de produtos do modelo`, persistedModelData);

            let formattedProductUrlsList: ProductsURLObject;
            const useHandle = persistedModelData.product_handles?.map(product_url => ({ product_url: `https://insiderstore.com.br/products/${product_url}` })) ?? [];
            const useFemale = persistedModelData.product_handles_female?.map(product_url => ({ product_url: `https://insiderstore.com.br/products/${product_url}` })) ?? [];
            const useMale = persistedModelData.product_handles_male?.map(product_url => ({ product_url: `https://insiderstore.com.br/products/${product_url}` })) ?? [];
            formattedProductUrlsList = {
                gender_collections: persistedModelData.gender_collections,
                product_handles: useHandle,
                product_handles_female: useFemale,
                product_handles_male: useMale
            }
            // const formattedProductUrlsList: ProductsUrlList = persistedModelData.product_handles?.map(product_url => ({ product_url: `http://127.0.0.1:9292/products/${product_url}` })) ?? [];
            Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] formattedProductUrlsList:`, formattedProductUrlsList);
            this.persistListInSlider(formattedProductUrlsList, "model");
            return
          }

          if(this.isHoldoutModel()) {
            if (this.dataset.statsigExperimentName == "dyo-homepage-collection-random-v0"){
              this.forceFallbackInSlider("propositalFallback");
            } else {
              this.hideSmartRecommenderSlider("holdout")
            }
            return;
          };

          if (this.useInsiderSmartRecommenderId) {
           const useInsiderData = await this.fetchUseInsiderCollectionData(this.useInsiderSmartRecommenderId)
           if(!useInsiderData) {
             this.hideSmartRecommenderSlider("useInsiderNotFound")
             Logger.warn(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] useInsiderData not found. Exiting.`, this.objExperimentInfos);
             return;
           }
           const formattedProductUrlsList: ProductsURLObject = {
              product_handles: useInsiderData?.products?.map(product => ({ product_url: product.url})) ?? []
           };
           useInsiderData?.products?.map(product => ({ product_url: product.url})) ?? [];
           this.persistListInSlider(formattedProductUrlsList, "useInsider");
           return;
          } 

          Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Nenhuma useInsiderSmartRecommenderId encontrada`);
          Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] force fallback`);
          // this.forceFallbackInSlider("propositalFallback");
          this.hideSmartRecommenderSlider("propositalFallback");

        } catch (error) {
          this.logError(`[DEBUG][Statsig][${this.statsigExperimentName}] Unexpected error in runTest()`, false, error);
        }
      }

      private loadModelFromStorage(): modelResponse | null {
        const storage = getSafeStorage();
        let parsed = null;
        try {
          const raw = storage.getItem(this.MODEL_KEY);
          parsed = raw ? JSON.parse(raw) : null;
        } catch {
          parsed = null;
        }
        if (!parsed || typeof parsed !== "object") return null;
        return parsed;
      }


      async fetchUseInsiderCollectionData(campaignId: string): Promise<UseInsiderCampaignData | null> {
        if (window && window.Insider) {
          async function waitForInsiderLoad() {
            return new Promise((resolve, reject) => {
              window.Insider.eventManager.once(
                "ins-sr:only-api-campaign:load",
                (event, data) => {
                  resolve({ event, data });
                }
              );
            });
          }

         return await waitForInsiderLoad().then(({ _, data }) => {
            if(data && data.campaignId === Number(campaignId)) return data
            return null
          });
        }
      }

      async fetchDOCollectionData() {
        if (this.servingMode === "inference_hub") {
          return this.fetchInferenceHubCollectionData();
        }

        if (!this.modelLink) {
          this.logError("[DEBUG][Statsig][DyoCollection] Missing PiChi2 modelUrl", false);
          return null;
        }

        const includeFields: PayloadField[] = [
            "customer_id",
            "stable_id",
            "allocated_experiment",
            "cartItemsIds",
            "cart",
            "utms"
          ];
        const payload = await buildModelRequestPayload({
          customer_id: this.objExperimentInfos.customer_id,
          stable_id: this.objExperimentInfos.stable_id,
          allocated_experiment: this.objExperimentInfos.allocated_experiment,
          cartItemsIds: this.objExperimentInfos.cart,
        }, includeFields);

        Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] payload:`, payload);

        try { 
          const data = await this.modelManager.getModel(
            this.modelLink,
            payload,
            { timeout: this.REQUEST_TIMEOUT_MS },
            this.MODEL_KEY,
            includeFields
          );
          Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Dados do modelo recebidos:`, data);
          return data;
        } catch (error) {
          this.logError(`[DEBUG][Statsig][${this.statsigExperimentName}] Failed to fetch DOCollectionData via ModelManager`, false, error);
          return null;
        }
      }

      async fetchInferenceHubCollectionData(): Promise<MinicartModelResponse | null> {
        if (!this.gatewayBaseUrl || !this.gatewayApiKey || !this.modelName) {
          this.logError("[DEBUG][Statsig][DyoCollection] Missing gatewayBaseUrl, gatewayApiKey or modelName for Inference Hub mode", false);
          return null;
        }

        const contextPayload = await buildModelRequestPayload(
          {
            customer_id: this.customerId ?? undefined,
            stable_id: this.objExperimentInfos.stable_id,
            allocated_experiment: this.objExperimentInfos.allocated_experiment,
          },
          ["utms", "products", "collections"]
        );

        const request = buildGatewayRequest({
          cart: await getCartData(),
          utms: contextPayload.utms,
          products: contextPayload.products,
          collections: contextPayload.collections,
          customerId: this.customerId,
          stableId: this.objExperimentInfos.stable_id,
          allocatedExperiment: this.objExperimentInfos.allocated_experiment,
        });

        Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Inference Hub Gateway payload:`, request);

        try {
          const response = await postGatewayRequest(this.modelName, this.gatewayBaseUrl, this.gatewayApiKey, request, this.REQUEST_TIMEOUT_MS);
          const normalized = normalizeMinicartInferenceHubResponse(response);
          if (!normalized) return null;

          this.persistInferenceHubModel(request, normalized);
          Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Inference Hub normalized data:`, normalized);
          return normalized;
        } catch (error) {
          this.logError(`[DEBUG][Statsig][${this.statsigExperimentName}] Failed to fetch Inference Hub data`, false, error as Error);
          return null;
        }
      }

      private persistInferenceHubModel(request: unknown, modelData: MinicartModelResponse): void {
        if (!this.MODEL_KEY) return;

        const storage = getSafeStorage();
        const savedAt = Date.now();
        const requestPayload = {
          timestamp: new Date().toISOString(),
          gatewayEndpoint: this.modelName ? buildGatewayEndpoint(this.modelName, this.gatewayBaseUrl) : null,
          modelName: this.modelName,
          usesFeatureView: this.usesFeatureView,
          payload: request,
        };

        try {
          storage.setItem(`${this.MODEL_KEY}:request`, JSON.stringify(requestPayload));
          storage.setItem(this.MODEL_KEY, JSON.stringify({ ...modelData, gatewayEndpoint: requestPayload.gatewayEndpoint, modelName: this.modelName, savedAt }));
        } catch (error) {
          Logger.warn(`[Statsig][${this.statsigExperimentName}] Failed to persist Inference Hub response using modelKey: ${this.MODEL_KEY}`, error);
        }
      }

      private isHoldoutModel(): boolean {
        return this.modelName === "holdout" || this.modelName === "dyo-minicart-recommender-holdout";
      }

      private isUseInsiderSmartRecommender(): boolean {
        return this.modelName === "useinsider_smart_recommender";
      }

      private stringLayerParam(value: unknown): string | null {
        return typeof value === "string" && value.length > 0 ? value : null;
      }

      private booleanLayerParam(value: unknown): boolean {
        return value === true || value === "true";
      }

      persistListInSlider(productsList: ProductsURLObject, origin: string) {
        const sliderElement = document.querySelector(`#${this.sliderId}`) as SliderElement
        if (sliderElement) {
          const timestamp = `dynamic:${origin}:${this.sliderId}-${new Date().toISOString()}`
          Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] Persist Product List: ${timestamp}`);
          sliderElement.dynamicData = productsList
          sliderElement.dataset.timestamp = timestamp

          // Decide se devemos aplicar a lógica de UTM nas tabs com base nos dados do modelo,
          // em vez de depender apenas da estrutura de DOM (mais performático).
          const hasGenderCollections =
            !!productsList.gender_collections ||
            (productsList.product_handles_female && productsList.product_handles_female.length > 0) ||
            (productsList.product_handles_male && productsList.product_handles_male.length > 0);

          if (this.utmGender && hasGenderCollections) {
            Logger.log(
              this.statsigExperimentName,
              `[DEBUG][Statsig][${this.statsigExperimentName}] persistListInSlider: aplicando UTM nas tabs (hasGenderCollections=${hasGenderCollections}, utm='${this.utmGender}')`
            );
            this.applyUtmGenderTabs();
          } else {
            Logger.log(
              this.statsigExperimentName,
              `[DEBUG][Statsig][${this.statsigExperimentName}] persistListInSlider: não aplicando UTM nas tabs (hasGenderCollections=${hasGenderCollections}, utm='${this.utmGender}')`
            );
          }

          return;
        }

        this.logError(`[DEBUG][Statsig][${this.statsigExperimentName}] Slider not founded`, true);
      }

      private applyUtmGenderTabs(attempt: number = 0) {
        if (!this.utmGender) {
          Logger.log(
            this.statsigExperimentName,
            `[DEBUG][Statsig][${this.statsigExperimentName}] applyUtmGenderTabs: sem utmGender, abortando.`
          );
          return;
        }

        const utmLower = this.utmGender.toLowerCase();

        // Root: usamos o próprio dyo-collection-slider e procuramos o dynamic-collection-slider dentro dele.
        const root =
          (this.querySelector("dynamic-collection-slider") as HTMLElement) ||
          (this.parentElement as HTMLElement) ||
          this;

        if (!root) {
          Logger.log(
            this.statsigExperimentName,
            `[DEBUG][Statsig][${this.statsigExperimentName}] applyUtmGenderTabs: root não encontrado.`
          );
          return;
        }

        const options = Array.from(
          root.querySelectorAll(".do-select_collection")
        ) as HTMLElement[];
        const collections = Array.from(
          root.querySelectorAll(".do-collection__item")
        ) as HTMLElement[];

        Logger.log(
          this.statsigExperimentName,
          `[DEBUG][Statsig][${this.statsigExperimentName}] applyUtmGenderTabs attempt=${attempt}, utm='${this.utmGender}', options=${options.length}, collections=${collections.length}`
        );

        // Se ainda não há opções/coleções renderizadas, tentamos novamente após um pequeno delay.
        if ((!options.length || !collections.length) && attempt < 10) {
          setTimeout(() => this.applyUtmGenderTabs(attempt + 1), 150);
          return;
        }

        if (!options.length || !collections.length) {
          Logger.log(
            this.statsigExperimentName,
            `[DEBUG][Statsig][${this.statsigExperimentName}] applyUtmGenderTabs: sem options/collections após retries, abortando.`
          );
          return;
        }

        // Mapeia a UTM para o índice da coleção.
        // Pela section, index 0 = Masculino, index 1 = Feminino.
        let targetIndex: string | null = null;

        if (utmLower.includes("fem")) {
          targetIndex = "collection-1";
        } else if (utmLower.includes("masc") || utmLower.includes("male")) {
          targetIndex = "collection-0";
        }

        Logger.log(
          this.statsigExperimentName,
          `[DEBUG][Statsig][${this.statsigExperimentName}] applyUtmGenderTabs: utmLower='${utmLower}', targetIndex='${targetIndex}'`
        );

        if (!targetIndex) {
          // Se não conseguimos mapear a UTM para uma coleção, mantemos o comportamento padrão.
          return;
        }

        let matchFound = false;

        options.forEach((option) => {
          option.classList.remove("active");
          if (option.dataset.index === targetIndex) {
            option.classList.add("active");
            matchFound = true;
          }
        });

        collections.forEach((collection) => {
          collection.classList.remove("active");
          if (collection.dataset.index === targetIndex) {
            collection.classList.add("active");
          }
        });

        Logger.log(
          this.statsigExperimentName,
          `[DEBUG][Statsig][${this.statsigExperimentName}] applyUtmGenderTabs: matchFound=${matchFound}`
        );

        if (!matchFound) {
          const firstOption = options[0];
          const firstCollection = collections[0];
          if (firstOption) firstOption.classList.add("active");
          if (firstCollection) firstCollection.classList.add("active");
        }
      }

      hideSmartRecommenderSlider(origin: string){
        Logger.log(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] hideSmartRecommenderSlider`);
        const sliderElementWrapper = document.querySelector(`.products-recommended-dyo`) as HTMLElement
        const sliderElement = document.querySelector(`#${this.sliderId}`) as SliderElement
        if (sliderElementWrapper) {
          sliderElementWrapper.style.display = "none"
          if(sliderElement) {
            const timestamp = `dynamic:${origin}:${this.sliderId}-${new Date().toISOString()}`
            sliderElement.useDynamicRecommender = false
            sliderElement.status = "forceNoRender"
            sliderElement.dataset.timestamp = timestamp
            sliderElement.dispatchEvent(
              new CustomEvent("dyo:dynamic-data", {
                bubbles: true,
                detail: {
                  origin,
                  timestamp,
                  status: sliderElement.status,
                },
              })
            );
          }
          return;
        }
      }

      forceFallbackInSlider(origin: string) {
        const sliderElement = document.querySelector(`#${this.sliderId}`) as SliderElement;

        if (sliderElement) {
          const timestamp = `dynamic:${origin}:${this.sliderId}-${new Date().toISOString()}`;
          Logger.log(
            this.statsigExperimentName,
            `[DEBUG][Statsig][${this.statsigExperimentName}] Forçando fallback no slider: ${timestamp}`
          );

          sliderElement.useDynamicRecommender = false;
          sliderElement.dynamicData = { product_handles: [] };
          sliderElement.status = "fallbackReady";
          sliderElement.dataset.timestamp = timestamp;

          sliderElement.dispatchEvent(
            new CustomEvent("dyo:dynamic-data", {
              bubbles: true,
              detail: {
                origin,
                timestamp,
                status: sliderElement.status,
              },
            })
          );

          return;
        }

        this.logError(`[DEBUG][Statsig][${this.statsigExperimentName}] Slider not founded`, true);
      }

      logError(message: string, showError: boolean = false, error?: Error) {
        Logger.error(this.statsigExperimentName, `[DEBUG][Statsig][${this.statsigExperimentName}] ${message}`, error);
      }
    }
  );